'use client';

import type { ReactNode } from 'react';
import type {
  DomainDto,
  ExternalNetworkEntityKind,
  GatewayDetailDto,
  HostDto,
  NetBlockDto,
  NetworkServiceDto,
  SoftwareDto,
  StorageServiceDto,
} from '@terra/shared-contracts-external-network';
import type { InternalNetworkAdEntityRelationshipDto } from '@terra/shared-contracts-internal-network';
import type { InternalNetworkAdEntityDetailDto } from '@terra/shared-contracts-internal-network';
import type { InternalNetworkAdEntityKind } from '@terra/ui-graphs/internal-network';
import { ENTITY_KIND_COLOR_MAP as INTERNAL_ENTITY_KIND_COLOR_MAP } from '@terra/ui-graphs/internal-network';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  DrawerCustomSection,
  DrawerGeneralDetails,
  DrawerTable,
  Text,
  type DrawerDetailRow,
} from '@terra/ui-design-system';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Link02Icon } from '@terra/ui-icons';
import {
  EXTERNAL_NETWORK_ENTITY_KIND_COLOR_MAP,
  getExternalNetworkEntityKindLabel,
  getInternalNetworkEntityKindLabel,
  type SurfaceEntityKindTranslator,
} from './surfaceAsset.constants';
import { buildStorageServiceOverviewContent } from './storageServiceOverviewContent';
import {
  buildGatewayOverviewContent,
  buildHostOverviewContent,
} from './hostGatewayOverviewContent';

type DateFormatter = {
  format: (date: Date) => string;
};

function humanizeSnakeCase(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function formatNetworkServiceLabel({
  port,
  applicationProtocol,
}: {
  port: number;
  applicationProtocol?: string | null;
}): string {
  return applicationProtocol ? `${port}/${applicationProtocol}` : String(port);
}

function formatRdata(rdata: string): string {
  try {
    const parsed = JSON.parse(rdata) as Record<string, unknown>;

    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${value}`)
        .join(' · ');
    }
  } catch {
    return rdata;
  }

  return rdata;
}

function EntityKindBadge({
  label,
  colorPalette,
}: {
  label: string;
  colorPalette: string;
}) {
  return (
    <Badge variant="outline" size="sm" colorPalette={colorPalette}>
      {label}
    </Badge>
  );
}

function DnsRecordRow({
  record,
}: {
  record: {
    rrType: string;
    rdata: string;
    ttl?: number | null;
  };
}) {
  return (
    <HStack
      gap={3}
      px={3}
      py={2}
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      alignItems="flex-start"
      w="full"
    >
      <Badge variant="outline" size="sm" flexShrink={0}>
        {record.rrType}
      </Badge>
      <Text fontSize="sm" fontFamily="mono" wordBreak="break-all">
        {formatRdata(record.rdata)}
      </Text>
      {record.ttl != null ? (
        <Text fontSize="xs" color="fg.muted" flexShrink={0}>
          TTL {record.ttl}
        </Text>
      ) : null}
    </HStack>
  );
}

type AssetDetailTranslator = (key: string) => string;

export type { AssetDetailTranslator };

function firstSeenLastSeenRows({
  createdAt,
  updatedAt,
  dateFormatter,
  t,
}: {
  createdAt: Date | string;
  updatedAt: Date | string;
  dateFormatter: DateFormatter;
  t: AssetDetailTranslator;
}) {
  return [
    {
      title: t('firstSeen'),
      value: dateFormatter.format(new Date(createdAt)),
    },
    {
      title: t('lastSeen'),
      value: dateFormatter.format(new Date(updatedAt)),
    },
  ] satisfies DrawerDetailRow[];
}

export function buildInternalOverviewRows({
  entity,
  entityKind,
  crownJewelControl,
  ownedPrincipalControl,
  t,
  tEntityKinds,
}: {
  entity: InternalNetworkAdEntityDetailDto;
  entityKind: InternalNetworkAdEntityKind;
  crownJewelControl: ReactNode;
  ownedPrincipalControl: ReactNode;
  t: AssetDetailTranslator;
  tEntityKinds: SurfaceEntityKindTranslator;
}): DrawerDetailRow[][] {
  return [
    [
      {
        title: t('displayName'),
        value: entity.displayName,
        withTooltip: true,
      },
      {
        title: t('type'),
        value: (
          <EntityKindBadge
            label={getInternalNetworkEntityKindLabel({
              kind: entityKind,
              t: tEntityKinds,
            })}
            colorPalette={INTERNAL_ENTITY_KIND_COLOR_MAP[entityKind]}
          />
        ),
      },
    ],
    [
      {
        title: 'sAMAccountName',
        value: entity.samAccountName ?? '—',
        withTooltip: Boolean(entity.samAccountName),
      },
      {
        title: t('crownJewel'),
        value: crownJewelControl,
      },
    ],
    [
      {
        title: t('ownedPrincipal'),
        value: ownedPrincipalControl,
      },
    ],
    [
      {
        title: t('distinguishedName'),
        value: entity.distinguishedName ?? '—',
        withTooltip: Boolean(entity.distinguishedName),
      },
    ],
    [
      {
        title: t('objectId'),
        value: entity.objectId,
        withTooltip: true,
      },
    ],
  ];
}

export function buildExternalOverviewContent({
  entityKind,
  asset,
  dateFormatter,
  t,
  tEntityKinds,
}: {
  entityKind: ExternalNetworkEntityKind;
  asset:
    | DomainDto
    | HostDto
    | GatewayDetailDto
    | NetworkServiceDto
    | NetBlockDto
    | SoftwareDto
    | StorageServiceDto;
  dateFormatter: DateFormatter;
  t: AssetDetailTranslator;
  tEntityKinds: SurfaceEntityKindTranslator;
}) {
  const kindLabel = getExternalNetworkEntityKindLabel({
    kind: entityKind,
    t: tEntityKinds,
  });
  const kindColor = EXTERNAL_NETWORK_ENTITY_KIND_COLOR_MAP[entityKind];
  const typeBadge = (
    <EntityKindBadge label={kindLabel} colorPalette={kindColor} />
  );
  const seenRows = firstSeenLastSeenRows({
    createdAt: asset.createdAt,
    updatedAt: asset.updatedAt,
    dateFormatter,
    t,
  });

  switch (entityKind) {
    case 'DOMAIN': {
      const domain = asset as DomainDto;

      return (
        <VStack gap={8} alignItems="stretch">
          <DrawerGeneralDetails
            title={t('generalDetails')}
            rows={[
              [
                { title: t('domain'), value: domain.fqdn, withTooltip: true },
                { title: t('type'), value: typeBadge },
              ],
              [
                {
                  title: t('topLevel'),
                  value: domain.isTopLevel ? t('yes') : t('no'),
                },
                { title: t('scope'), value: domain.scope },
              ],
              seenRows,
            ]}
          />
          {domain.dnsRecords.length > 0 ? (
            <DrawerCustomSection title={t('dnsRecords')}>
              <VStack gap={2} alignItems="stretch" w="full">
                {domain.dnsRecords.map((record, index) => (
                  <DnsRecordRow
                    key={`${record.rrType}-${record.rdata}-${index}`}
                    record={record}
                  />
                ))}
              </VStack>
            </DrawerCustomSection>
          ) : null}
        </VStack>
      );
    }

    case 'HOST': {
      return buildHostOverviewContent({
        host: asset as HostDto,
        typeBadge,
        seenRows,
        t,
      });
    }

    case 'GATEWAY': {
      return buildGatewayOverviewContent({
        gateway: asset as GatewayDetailDto,
        typeBadge,
        seenRows,
        t,
      });
    }

    case 'NETWORK_SERVICE': {
      const service = asset as NetworkServiceDto;

      return (
        <VStack gap={8} alignItems="stretch">
          <DrawerGeneralDetails
            title={t('generalDetails')}
            rows={[
              [
                {
                  title: t('service'),
                  value: formatNetworkServiceLabel({
                    port: service.port,
                    applicationProtocol: service.applicationProtocol,
                  }),
                },
                { title: t('type'), value: typeBadge },
              ],
              [
                {
                  title: t('transport'),
                  value: (
                    <Badge size="sm" variant="outline">
                      {service.transportProtocol}
                    </Badge>
                  ),
                },
                {
                  title: t('tls'),
                  value: service.tlsEnabled ? (
                    <Badge variant="outline" size="sm" colorPalette="green">
                      {t('enabled')}
                    </Badge>
                  ) : (
                    <Text fontSize="sm" color="fg.muted">
                      {t('disabled')}
                    </Text>
                  ),
                },
              ],
              ...(service.hostIp || service.gatewayHostname
                ? ([
                    [
                      ...(service.hostIp
                        ? [{ title: t('host'), value: service.hostIp }]
                        : []),
                      ...(service.gatewayHostname
                        ? [
                            {
                              title: t('gateway'),
                              value: service.gatewayHostname,
                            },
                          ]
                        : []),
                    ],
                  ] satisfies DrawerDetailRow[][])
                : []),
              seenRows,
            ]}
          />
          {service.banner ? (
            <DrawerCustomSection title={t('banner')}>
              <Text
                fontSize="xs"
                fontFamily="mono"
                color="fg.muted"
                whiteSpace="pre-wrap"
                wordBreak="break-all"
              >
                {service.banner}
              </Text>
            </DrawerCustomSection>
          ) : null}
        </VStack>
      );
    }

    case 'NETBLOCK': {
      const netblock = asset as NetBlockDto;

      return (
        <DrawerGeneralDetails
          title={t('generalDetails')}
          rows={[
            [
              { title: t('cidr'), value: netblock.cidr },
              { title: t('type'), value: typeBadge },
            ],
            [
              { title: t('scope'), value: netblock.scope },
              {
                title: t('source'),
                value: humanizeSnakeCase(netblock.sourceType),
              },
            ],
            seenRows,
          ]}
        />
      );
    }

    case 'SOFTWARE': {
      const software = asset as SoftwareDto;
      const vendorDomainRow: DrawerDetailRow[] = [
        ...(software.vendor
          ? [{ title: t('vendor'), value: software.vendor }]
          : []),
        ...(software.domainName
          ? [{ title: t('domain'), value: software.domainName }]
          : []),
      ];

      return (
        <DrawerGeneralDetails
          title={t('generalDetails')}
          rows={[
            [
              { title: t('product'), value: software.product },
              { title: t('type'), value: typeBadge },
            ],
            [
              {
                title: t('softwareType'),
                value: humanizeSnakeCase(software.softwareType),
              },
              { title: t('version'), value: software.version ?? '—' },
            ],
            ...(vendorDomainRow.length > 0 ? [vendorDomainRow] : []),
            seenRows,
          ]}
        />
      );
    }

    case 'STORAGE_SERVICE':
      return buildStorageServiceOverviewContent({
        storage: asset as StorageServiceDto,
        typeBadge,
        seenRows,
        t,
      });
    default:
      return null;
  }
}

type RelationshipRow = {
  id: string;
  relationshipType: string;
  direction: string;
  peerDisplayName: string;
  peerEntityKind: string;
  isAbusable: boolean;
};

export function buildRelationshipColumnDefs(): ColDef<RelationshipRow>[] {
  return [
    {
      colId: 'relationshipType',
      headerName: 'Type',
      field: 'relationshipType',
      flex: 1,
    },
    {
      colId: 'direction',
      headerName: 'Direction',
      field: 'direction',
      flex: 1,
    },
    {
      colId: 'peerDisplayName',
      headerName: 'Peer',
      field: 'peerDisplayName',
      flex: 2,
    },
    {
      colId: 'peerEntityKind',
      headerName: 'Peer kind',
      field: 'peerEntityKind',
      flex: 1,
    },
    {
      colId: 'isAbusable',
      headerName: 'Abusable',
      field: 'isAbusable',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<RelationshipRow>) =>
        params.value ? (
          <Badge variant="fill" size="sm" colorPalette="red">
            Yes
          </Badge>
        ) : null,
    },
  ];
}

export function mapRelationshipsToRows(
  relationships: InternalNetworkAdEntityRelationshipDto[],
): RelationshipRow[] {
  return relationships.map((relationship, index) => ({
    id: `${relationship.relationshipType}-${relationship.peerNodeId}-${index}`,
    relationshipType: relationship.relationshipType,
    direction: relationship.direction,
    peerDisplayName: relationship.peerDisplayName ?? relationship.peerNodeId,
    peerEntityKind: relationship.peerEntityKind ?? '',
    isAbusable: relationship.isAbusable,
  }));
}

export function RelationshipsSection({
  relationships,
}: {
  relationships: InternalNetworkAdEntityRelationshipDto[];
}) {
  const rowData = mapRelationshipsToRows(relationships);
  const columnDefs = buildRelationshipColumnDefs();

  if (rowData.length === 0) {
    return (
      <Box p={6}>
        <VStack gap={3} alignItems="center" py={8}>
          <Link02Icon size={24} color="var(--colors-fg-muted)" />
          <Text fontSize="sm" fontWeight="medium" color="fg.default">
            No relationships found
          </Text>
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            Relationships appear after graph discovery completes.
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <DrawerTable<RelationshipRow>
        title="Relationships"
        rowData={rowData}
        columnDefs={columnDefs}
      />
    </Box>
  );
}
